import { gatewayApi as api } from './gatewayApi';

// Work Instructions Types
export interface WorkInstruction {
  Id?: string;
  Name: string;
  Description?: string;
  Active: boolean;
  Document?: Document;
  DocId?: string;
  CreatedDate?: string;
  UpdatedDate?: string;
}

export interface Document {
  Id?: string;
  Name: string;
  DocumentType?: string;
  Docfile?: string; // Base64 encoded file data
  DocFile?: string; // File URL for downloads
  Size?: number;
  CreatedDate?: string;
}

export interface CreateWorkInstructionRequest {
  Name: string;
  Description?: string;
  Active: boolean;
  Document?: DocumentUpload;
}

export interface UpdateWorkInstructionRequest extends Partial<CreateWorkInstructionRequest> {
  Id: string;
}

export interface DocumentUpload {
  Name: string;
  DocumentType: string;
  Docfile: string; // Base64 encoded file data
}

// Response Types
export interface WorkInstructionsResponse {
  data: WorkInstruction[];
  total: number;
  page: number;
  limit: number;
}

export interface WorkInstructionStatistics {
  total: number;
  active: number;
  inactive: number;
  withDocuments: number;
  withoutDocuments: number;
}

// Work Instructions API Service
export const workInstructionsApi = {
  // Work Instructions CRUD operations
  getAll: (params?: { 
    page?: number; 
    limit?: number; 
    search?: string; 
    active?: boolean;
    hasDocument?: boolean;
  }): Promise<WorkInstructionsResponse> =>
    api.get<WorkInstructionsResponse>('/workinstruction', params),

  getById: (id: string): Promise<WorkInstruction> =>
    api.get<WorkInstruction>(`/workinstruction/${id}`),

  create: (workInstruction: CreateWorkInstructionRequest): Promise<WorkInstruction> =>
    api.post<WorkInstruction>('/workinstruction', workInstruction),

  update: (id: string, workInstruction: UpdateWorkInstructionRequest): Promise<WorkInstruction> =>
    api.put<WorkInstruction>(`/workinstruction/${id}`, workInstruction),

  delete: (id: string): Promise<void> =>
    api.delete<void>(`/workinstruction/${id}`),

  search: (filters: Record<string, any>): Promise<WorkInstruction[]> =>
    api.post<WorkInstruction[]>('/workinstruction/search', { filter: filters }),

  // Document operations
  uploadDocument: (file: File): Promise<Document> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result as string;
        const documentData: DocumentUpload = {
          Name: file.name,
          DocumentType: file.type,
          Docfile: base64Data,
        };
        
        // Upload to document endpoint
        api.post<Document>('/document', documentData)
          .then(resolve)
          .catch(reject);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  },

  downloadDocument: (documentId: string): Promise<Blob> =>
    api.getBlob(`/document/download/${documentId}`),

  getDocumentById: (documentId: string): Promise<Document> =>
    api.get<Document>(`/document/${documentId}`),

  // Statistics
  getStatistics: (): Promise<WorkInstructionStatistics> =>
    api.get<WorkInstructionStatistics>('/workinstruction/statistics'),

  // Bulk operations
  bulkActivate: (workInstructionIds: string[]): Promise<void> =>
    api.post<void>('/workinstruction/bulk/activate', { ids: workInstructionIds }),

  bulkDeactivate: (workInstructionIds: string[]): Promise<void> =>
    api.post<void>('/workinstruction/bulk/deactivate', { ids: workInstructionIds }),

  // Export
  export: (): Promise<Blob> =>
    api.getBlob('/workinstruction/export'),

  // Validation helpers
  validation: {
    validateWorkInstruction: (workInstruction: Partial<CreateWorkInstructionRequest> | Partial<UpdateWorkInstructionRequest> | Partial<WorkInstruction>): string[] => {
      const errors: string[] = [];
      
      if (!workInstruction.Name?.trim()) {
        errors.push('Work Instruction Name is required');
      }
      
      return errors;
    },

    validateDocument: (file: File): string[] => {
      const errors: string[] = [];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (file.size > maxSize) {
        errors.push('File size must be less than 10MB');
      }
      
      // Check file type - allow common document formats
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'image/jpeg',
        'image/png',
        'image/gif'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        errors.push('File type not supported. Please upload PDF, Word, Text, or Image files.');
      }
      
      return errors;
    },

    prepareForSave: (formData: any): CreateWorkInstructionRequest | UpdateWorkInstructionRequest => {
      const workInstruction = { ...formData };
      
      // Handle document data - already processed by uploadDocument if present
      // No additional processing needed here
      
      return workInstruction;
    },
  },

  // Helper functions
  utils: {
    downloadDocumentFile: async (doc: Document): Promise<void> => {
      try {
        let fileUrl: string;
        let fileName: string = doc.Name || 'document';

        if (doc.DocFile) {
          // Direct URL available
          fileUrl = doc.DocFile;
        } else if (doc.Id) {
          // Need to fetch from API
          const blob = await workInstructionsApi.downloadDocument(doc.Id);
          fileUrl = window.URL.createObjectURL(blob);
        } else {
          throw new Error('No document URL or ID available');
        }

        // Create download link
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up object URL if created
        if (!doc.DocFile && doc.Id) {
          window.URL.revokeObjectURL(fileUrl);
        }
      } catch (error) {
        console.error('Failed to download document:', error);
        throw error;
      }
    },

    formatFileSize: (bytes: number): string => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    getFileIcon: (fileType: string): string => {
      if (fileType.includes('pdf')) return '📄';
      if (fileType.includes('word') || fileType.includes('document')) return '📝';
      if (fileType.includes('image')) return '🖼️';
      if (fileType.includes('text')) return '📃';
      return '��';
    },
  },
}; 